import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual } from 'react-redux';

import getDesignDataForExport from 'editor/src/store/design/selector/getDesignDataForExport';
import getVariantWarnings from 'editor/src/store/editorModules/warnings/selector/getVariantWarnings';
import { setVariantWarningsAction } from 'editor/src/store/editorModules/warnings/slice';
import calculateDesignDataWarnings from 'editor/src/store/editorModules/warnings/utils/calculateDesignDataWarnings';
import {
  extendedWarningsToExportedWarnings,
  extendedWarningsToWarnings,
} from 'editor/src/store/editorModules/warnings/utils/warningMapper';
import getGalleryImages from 'editor/src/store/gallery/selector/getGalleryImages';
import { useStore, useDispatch, useSelector } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';
import getVariationGroupsReadiness from 'editor/src/store/variants/helpers/getVariationGroupsReadiness';
import { getSizeSpecificAreas, getVariationSize } from 'editor/src/store/variants/helpers/sizeSpecificAreas';
import checkForMissingTemplatesOperation from 'editor/src/store/variants/operation/checkForMissingTemplatesOperation';
import getProducts from 'editor/src/store/variants/selector/getProducts';
import { ExportVariationGroup, VariationGroup } from 'editor/src/store/variants/types';

import { captureException } from 'editor/src/logger';
import formatDesignDataForLogging from 'editor/src/util/formatDesignDataForLogging';
import sendPostMessage from 'editor/src/util/postMessages/sendPostMessage';

// Slightly longer than the 5s request-dedup TTL inside
// `checkForMissingTemplatesOperation` so a retry is never suppressed by its own
// previous attempt.
const MISSING_TEMPLATES_RETRY_INTERVAL = 6000;

function useVariantWatcher() {
  const store = useStore();
  const dispatch = useDispatch();
  const { variationGroups, isVariantFlow, enabledWarnings, designOptionsControl } = useSelector(
    (state) => ({
      isVariantFlow: state.variants.isVariantFlow,
      variationGroups: state.variants.variationGroups,
      enabledWarnings: state.hostSettings.enabledWarnings,
      designOptionsControl: state.variants.designOptionsControl,
    }),
    shallowEqual,
  );

  const products = useSelector(getProducts);
  const selectedProductId = useSelector((state) => state.variants.selectedProductId);
  const sizeControlKey = useSelector((state) => state.variants.product.sizeControlKey);
  const sizeSpecificAreas = useSelector((state) => state.variants.product.sizeSpecificAreas);

  const productsVariationGroups = useMemo(() => {
    return !products
      ? undefined
      : Object.entries(products).reduce<{ [productId: string]: VariationGroup[] }>(
          (multipleVariationGroups, [productId, productData]) => {
            multipleVariationGroups[productId] = productData.variantGroups;
            return multipleVariationGroups;
          },
          {},
        );
  }, [products]);

  const galleryInitialized = useSelector((state) => getGalleryImages(state).length > 0);

  // GAPI-47583 — last `pending` value sent to the host, so we only post on change.
  const pendingSentRef = useRef<boolean | undefined>(undefined);
  const [pendingState, setPendingState] = useState(false);
  const sendPendingState = useCallback((pending: boolean) => {
    if (pendingSentRef.current === pending) {
      return;
    }
    pendingSentRef.current = pending;
    setPendingState(pending);
    sendPostMessage('variants.variationGroupsPending', { pending });
  }, []);

  // GAPI-47583 — while variants wait for design templates, keep re-asking the
  // host. A single `variants.requestDesignTemplates` can be lost (the host
  // swallows a failed template fetch and the editor never re-sends on its own),
  // leaving the affected variants without designData forever. The interval is
  // self-terminating: once the templates land, `checkForMissingTemplates` finds
  // nothing to fetch and `pending` goes back to false.
  useEffect(() => {
    if (!pendingState) {
      return undefined;
    }
    const intervalId = window.setInterval(() => {
      dispatch(checkForMissingTemplatesOperation([]));
    }, MISSING_TEMPLATES_RETRY_INTERVAL);
    return () => window.clearInterval(intervalId);
  }, [pendingState]);

  const getExportVariationGroups = useCallback(
    (variationGroups: VariationGroup[], state: RootState, updateWarnings = true) => {
      const { hasAllDesignData, hasStaleDesignData } = getVariationGroupsReadiness(variationGroups);
      const { images } = state.gallery;
      const addons = state.editorModules.addons.inUse;
      const sizeSpecific = getSizeSpecificAreas(state.variants.product).length > 0;

      const exportedVariationGroups = variationGroups.map((group) => ({
        title: group.title,
        linked: group.linked,
        variations: group.variationsInfo.map(({ variation, designData }) => {
          if (designData && variation.productUid !== designData.product_uid) {
            captureException(new Error('Product UID mismatch'), {
              extra: {
                variation,
                designData: formatDesignDataForLogging(designData),
              },
            });
          }

          const extendedWarnings = designData ? calculateDesignDataWarnings(designData, state) : [];
          if (updateWarnings) {
            const warnings = extendedWarningsToWarnings(extendedWarnings);
            const warningsChanged = !isEqual(warnings, getVariantWarnings(state, variation.productUid));
            if (warningsChanged) {
              dispatch(
                setVariantWarningsAction({
                  productId: variation.productUid,
                  warnings,
                }),
              );
            }
          }

          const exportDesign = designData
            ? getDesignDataForExport(
                designData,
                images,
                addons,
                extendedWarningsToExportedWarnings(extendedWarnings),
                state.hostSettings.cmykOnly,
              )
            : undefined;

          const size = sizeSpecific ? getVariationSize(variation, state.variants.product)?.title : undefined;
          return {
            productUid: variation.productUid,
            designDataJSON: exportDesign ? JSON.stringify(exportDesign) : undefined,
            dimensions: designData?.related_dimensions || designData?.dimensions,
            pageCount: designData?.page_count_limit ? designData?.page_count : undefined,
            ...(size ? { size } : {}),
          };
        }),
        designOptions: group.designOptions,
      }));

      return {
        exportedVariationGroups,
        hasAllDesignData,
        hasStaleDesignData,
      };
    },
    [],
  );

  useEffect(() => {
    // single product flow
    if (!isVariantFlow || (productsVariationGroups && Object.keys(productsVariationGroups).length)) {
      return;
    }

    const state = store.getState();
    const { exportedVariationGroups, hasAllDesignData, hasStaleDesignData } = getExportVariationGroups(
      variationGroups,
      state,
    );

    if (hasAllDesignData && exportedVariationGroups.length > 0) {
      sendPostMessage('variants.variationGroups', exportedVariationGroups);
    }

    sendPendingState(exportedVariationGroups.length > 0 && (!hasAllDesignData || hasStaleDesignData));
  }, [isVariantFlow, variationGroups, enabledWarnings, galleryInitialized, productsVariationGroups]);

  useEffect(() => {
    if (!isVariantFlow) {
      return;
    }

    sendPostMessage('variants.designOptionControls', designOptionsControl ?? []);
  }, [isVariantFlow, designOptionsControl]);

  useEffect(() => {
    if (!isVariantFlow || !sizeControlKey) {
      return;
    }

    sendPostMessage('variants.sizeSpecificAreas', { areas: sizeSpecificAreas ?? [], sizeControlKey });
  }, [isVariantFlow, sizeSpecificAreas, sizeControlKey]);

  useEffect(() => {
    // multiple flow
    if (!isVariantFlow || !productsVariationGroups) {
      return;
    }

    const state = store.getState();
    let productsHasAllDesignData = true;
    let productsHaveStaleDesignData = false;

    const allProductsExportGroups = Object.entries(productsVariationGroups).reduce<{
      [productId: string]: Array<ExportVariationGroup>;
    }>((productsExportGroups, [productId, productItemVariationGroups]) => {
      const { exportedVariationGroups, hasAllDesignData, hasStaleDesignData } =
        productId !== selectedProductId
          ? getExportVariationGroups(productItemVariationGroups, state, false)
          : getExportVariationGroups(variationGroups, state, true);

      productsHasAllDesignData = productsHasAllDesignData && hasAllDesignData;
      productsHaveStaleDesignData = productsHaveStaleDesignData || hasStaleDesignData;
      productsExportGroups[productId] = exportedVariationGroups as ExportVariationGroup[];
      return productsExportGroups;
    }, {});

    const hasExportGroups = Object.values(allProductsExportGroups).some((groups) => groups.length > 0);

    if (productsHasAllDesignData && Object.keys(allProductsExportGroups).length) {
      sendPostMessage('variants.multipleVariationGroups', allProductsExportGroups);
    }

    sendPendingState(hasExportGroups && (!productsHasAllDesignData || productsHaveStaleDesignData));
  }, [isVariantFlow, productsVariationGroups, selectedProductId, variationGroups, galleryInitialized, enabledWarnings]);
}

export default useVariantWatcher;
